import { History, Save } from 'lucide-react'
import { Button } from '../../../../components'

interface ConfigurationFooterProps {
  isDirty: boolean
  isValid?: boolean
  onReset: () => void
  onSave: () => void
  isSubmitting?: boolean
  isReadOnly?: boolean
  lastUpdatedBy?: string
  lastUpdatedAt?: string
}

export function ConfigurationFooter({
  isDirty,
  isValid = true,
  onReset,
  onSave,
  isSubmitting = false,
  isReadOnly = false,
  lastUpdatedBy,
  lastUpdatedAt,
}: ConfigurationFooterProps) {
  const canSave = isDirty && isValid && !isSubmitting && !isReadOnly

  return (
    <div className="bg-white rounded-2xl border border-slate-200 px-5 py-4 flex items-center justify-between flex-wrap gap-3 mt-5 min-w-0 font-sans">
      {/* Left info */}
      <div className="flex items-center gap-2 text-xs text-slate-500 min-w-0">
        <History className="h-4 w-4 text-slate-400 shrink-0" />
        <span className="truncate">
          {isReadOnly ? (
            <span className="text-amber-700 font-medium">Read-only mode (insufficient permissions to save)</span>
          ) : lastUpdatedBy || lastUpdatedAt ? (
            <>
              Configured {lastUpdatedBy ? <>by <strong className="font-bold text-slate-700">{lastUpdatedBy}</strong></> : null}
              {lastUpdatedAt ? <> on {lastUpdatedAt}</> : null}
            </>
          ) : (
            <span>Settings synchronized with institutional database</span>
          )}
        </span>
      </div>

      {/* Right buttons */}
      <div className="flex items-center gap-3 shrink-0 justify-end">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onReset}
          disabled={!isDirty || isSubmitting || isReadOnly}
          className="bg-transparent hover:bg-slate-100 text-slate-600 border-none h-auto px-3 py-2 text-xs font-semibold shadow-none rounded-lg cursor-pointer"
        >
          Reset to Server Values
        </Button>

        <Button
          type="submit"
          variant="primary"
          size="sm"
          icon={<Save className="h-4 w-4" />}
          onClick={onSave}
          disabled={!canSave}
          className="bg-teal-900 hover:bg-teal-950 text-white rounded-lg px-5 py-2.5 text-xs font-semibold shadow-xs flex items-center gap-2 border-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  )
}
